/**
 * batch-70 wagenpark-week (FO-528, spec 228) - pure rekenregels van de wekelijkse
 * km-standen. I/O-vrij: de API-laag laadt de data en roept deze functies aan.
 *
 *   1. ISO-weken in Europe/Amsterdam (sleutel jjjjww, label jjjj-Www);
 *   2. plausibiliteit van een nieuwe stand (`isKmStandPlausibel`): teller terug of
 *      een sprong boven het weekmaximum is verdacht, nooit stil geaccepteerd;
 *   3. de km van een week uit de standen (`berekenWeekKm`): telematica wint, anders
 *      de doorgegeven standen, anders een interpolatie (een schatting, gemarkeerd);
 *   4. de contract-trend (`berekenContractTrend`): rato, afwijking, prognose en de
 *      meer- of minderkm met hun verwachte bedrag;
 *   5. de verdeling privé/zakelijk (`verdeelWeekKm`): de telematica-classificatie
 *      wint, anders afgeleid uit de werkkilometers en woon-werk; nooit negatief;
 *   6. het weektempo en de geschatte onderhoudsdatum (`schatOnderhoudsdatum`).
 *
 * Elke uitkomst draagt zijn bron. Geen oordeel: "verdacht" en "boven de drempel"
 * zijn vergelijkingen met een instelling.
 */

import {
  type KmTrendBron,
  type KmTrendMarkering,
  type RitClassificatie,
  type VerdelingBron,
  type PriveKmSignaal,
  type TankRegime,
  type ContractJaarDTO,
  type ContractJaarStatus,
  type WerkAdviesSoort,
} from '@streep/types'
import { amsterdamAllDayStartToUtc, amsterdamWallclockToUtc, utcToAmsterdamWallclock } from './amsterdam-tijd'
import { mondayOfIsoWeek } from './iso-week'
import { kmStandOpMoment, type KmAnker, type KmStandInput } from './km-balans'

const DAG_MS = 86_400_000
const rond1 = (n: number): number => Math.round(n * 10) / 10
const rond2 = (n: number): number => Math.round(n * 100) / 100

// ─── 1. ISO-weken (Europe/Amsterdam) ─────────────────────────────────────────

function ymdPlus(ymd: string, dagen: number): string {
  const d = new Date(Date.UTC(Number(ymd.slice(0, 4)), Number(ymd.slice(5, 7)) - 1, Number(ymd.slice(8, 10))))
  d.setUTCDate(d.getUTCDate() + dagen)
  return d.toISOString().slice(0, 10)
}

/** ISO-jaar en -week van een NL-kalenderdag (jjjj-mm-dd). */
export function isoWeekVanDag(ymd: string): { jaar: number; week: number } {
  const d = new Date(Date.UTC(Number(ymd.slice(0, 4)), Number(ymd.slice(5, 7)) - 1, Number(ymd.slice(8, 10))))
  const dow = d.getUTCDay() || 7
  d.setUTCDate(d.getUTCDate() + 4 - dow)
  const jaar = d.getUTCFullYear()
  const week = Math.ceil(((d.getTime() - Date.UTC(jaar, 0, 1)) / DAG_MS + 1) / 7)
  return { jaar, week }
}

/** Week-sleutel (jjjjww) van een moment, als NL-kalenderdag gelezen. */
export function weekSleutelVan(moment: Date): number {
  const { jaar, week } = isoWeekVanDag(utcToAmsterdamWallclock(moment).ymd)
  return jaar * 100 + week
}

export function weekLabelVanSleutel(sleutel: number): string {
  return `${Math.floor(sleutel / 100)}-W${String(sleutel % 100).padStart(2, '0')}`
}

/** `2026-W44` -> 202644. Gooit bij een ongeldig label. */
export function weekSleutelUitLabel(label: string): number {
  const m = /^(\d{4})-W(\d{2})$/.exec(label.trim())
  if (!m) throw new Error(`Ongeldige week: "${label}" (verwacht jjjj-Www)`)
  return Number(m[1]) * 100 + Number(m[2])
}

/** De maandag (jjjj-mm-dd) van een week-sleutel. */
export function maandagVanWeek(sleutel: number): string {
  return mondayOfIsoWeek(sleutel % 100, Math.floor(sleutel / 100)).toISOString().slice(0, 10)
}

export interface WeekGrenzen {
  /** Maandag 00:00 NL (UTC). */
  start: Date
  /** Volgende maandag 00:00 NL (UTC), exclusief. */
  eind: Date
  /** Zondag 23:59 NL (UTC): het peilmoment van de weekstand. */
  zondagEinde: Date
  /** De zeven NL-dagen ma-zo (jjjj-mm-dd). */
  dagen: string[]
}

export function weekGrenzen(sleutel: number): WeekGrenzen {
  const maandag = maandagVanWeek(sleutel)
  const zondag = ymdPlus(maandag, 6)
  return {
    start: amsterdamAllDayStartToUtc(maandag),
    eind: amsterdamAllDayStartToUtc(ymdPlus(maandag, 7)),
    zondagEinde: amsterdamWallclockToUtc(zondag, '23:59'),
    dagen: Array.from({ length: 7 }, (_, i) => ymdPlus(maandag, i)),
  }
}

export function vorigeWeekSleutel(sleutel: number): number {
  return weekSleutelVan(new Date(weekGrenzen(sleutel).start.getTime() - DAG_MS / 2))
}

export function volgendeWeekSleutel(sleutel: number): number {
  return weekSleutelVan(new Date(weekGrenzen(sleutel).eind.getTime() + DAG_MS / 2))
}

/** De afgesloten week vóór het moment (op maandag 05:00 = de week die zondag eindigde). */
export function afgeslotenWeekVoor(moment: Date): number {
  return vorigeWeekSleutel(weekSleutelVan(moment))
}

/** Alle week-sleutels van `van` tot en met `tot` (oplopend). */
export function weekSleutelsTussen(van: number, tot: number): number[] {
  const uit: number[] = []
  let w = van
  for (let i = 0; i < 600 && w <= tot; i++) {
    uit.push(w)
    w = volgendeWeekSleutel(w)
  }
  return uit
}

// ─── 2. Plausibiliteit ───────────────────────────────────────────────────────

export interface KmPlausibiliteit {
  plausibel: boolean
  soort: 'TELLER_TERUG' | 'SPRONG' | null
  /** Km per week tussen de vorige en de nieuwe stand (null zonder vorige). */
  kmPerWeek: number | null
}

/**
 * Past een nieuwe stand in de reeks? Lager dan de vorige = teller terug; meer dan
 * `maxKmPerWeek` per week (naar rato van de tijd ertussen, minimaal een week) =
 * sprong. Zonder vorige stand is elke stand plausibel (het begin van de reeks).
 * Verdacht betekent: niet stil accepteren en niet stil weggooien (FO-528 §A3).
 */
export function isKmStandPlausibel(input: {
  kmStand: number
  op: Date
  vorige: { kmStand: number; op: Date } | null
  maxKmPerWeek: number
}): KmPlausibiliteit {
  if (!input.vorige) return { plausibel: true, soort: null, kmPerWeek: null }
  const verschil = input.kmStand - input.vorige.kmStand
  const weken = Math.max(1, (input.op.getTime() - input.vorige.op.getTime()) / (7 * DAG_MS))
  const kmPerWeek = Math.round(verschil / weken)
  if (verschil < 0) return { plausibel: false, soort: 'TELLER_TERUG', kmPerWeek }
  if (verschil > input.maxKmPerWeek * weken) return { plausibel: false, soort: 'SPRONG', kmPerWeek }
  return { plausibel: true, soort: null, kmPerWeek }
}

// ─── 3. De km van een week ───────────────────────────────────────────────────

/** Bronnen die als telematica gelden (de hoogste betrouwbaarheid). */
export const TELEMATICA_KM_BRONNEN: readonly string[] = ['TELEMATICA', 'IMPORT']

export interface WeekKmUitkomst {
  weekKm: number | null
  kmStandBegin: number | null
  kmStandEind: number | null
  bron: KmTrendBron
  zekerheid: 'HOOG' | 'MIDDEL' | 'LAAG' | null
}

const WEEK_MEET = { nabijDagen: 1, maxIntervalDagen: 21 } as const

function isExact(a: KmAnker): boolean {
  return a.methode === 'NABIJ' && a.afstandDagen <= 1
}

/**
 * De km van een week: het verschil tussen de stand op zondag 23:59 van de vorige
 * week en die van deze week. Volgorde (de eerste die beide grenzen kent wint):
 *   1. alleen telematica-/importstanden (TELEMATICA of IMPORT) - een meting;
 *   2. alle doorgegeven standen, op of vlak naast de grenzen - een meting (STANDEN);
 *   3. alle standen, lineair tussen standen rond de grenzen - een schatting
 *      (GEINTERPOLEERD, lagere zekerheid);
 *   4. anders GEEN.
 * De standen moeten al gefilterd zijn op plausibiliteit (live registraties; een
 * verdachte stand staat in quarantaine en komt hier niet in).
 */
export function berekenWeekKm(input: { standen: readonly KmStandInput[]; weekSleutel: number }): WeekKmUitkomst {
  const g = weekGrenzen(input.weekSleutel)
  const begin = new Date(g.start.getTime() - 60_000)
  const eind = g.zondagEinde
  const gesorteerd = [...input.standen].sort((a, b) => a.op.getTime() - b.op.getTime() || a.kmStand - b.kmStand)
  const meet = (reeks: readonly KmStandInput[]) => {
    const b = kmStandOpMoment(reeks, begin, WEEK_MEET)
    const e = kmStandOpMoment(reeks, eind, WEEK_MEET)
    return b && e ? { b, e } : null
  }
  const telematica = gesorteerd.filter((s) => TELEMATICA_KM_BRONNEN.includes(s.bron))
  const tm = meet(telematica)
  if (tm && isExact(tm.b) && isExact(tm.e) && !(tm.b.standen[0] === tm.e.standen[0])) {
    const bron: KmTrendBron = tm.e.standen[0]?.bron === 'IMPORT' ? 'IMPORT' : 'TELEMATICA'
    return uitkomst(tm.b, tm.e, bron, 'HOOG')
  }
  const alle = meet(gesorteerd)
  if (!alle || (alle.b.standen.length === 1 && alle.e.standen.length === 1 && alle.b.standen[0] === alle.e.standen[0])) {
    return { weekKm: null, kmStandBegin: null, kmStandEind: null, bron: 'GEEN', zekerheid: null }
  }
  if (isExact(alle.b) && isExact(alle.e)) return uitkomst(alle.b, alle.e, 'STANDEN', 'HOOG')
  const verst = Math.max(alle.b.afstandDagen, alle.e.afstandDagen)
  return uitkomst(alle.b, alle.e, 'GEINTERPOLEERD', verst <= 7 ? 'MIDDEL' : 'LAAG')
}

function uitkomst(b: KmAnker, e: KmAnker, bron: KmTrendBron, zekerheid: 'HOOG' | 'MIDDEL' | 'LAAG'): WeekKmUitkomst {
  return {
    weekKm: Math.max(0, Math.round(e.km - b.km)),
    kmStandBegin: Math.round(b.km),
    kmStandEind: Math.round(e.km),
    bron,
    zekerheid,
  }
}

// ─── 4. Contract-trend ───────────────────────────────────────────────────────

/** Maanden (met fractie) tussen twee momenten op NL-kalenderdagen: 18 hele maanden = 18. */
export function maandenTussen(van: Date, tot: Date): number {
  const a = utcToAmsterdamWallclock(van).ymd
  const b = utcToAmsterdamWallclock(tot).ymd
  if (b <= a) return 0
  const [ja, ma, da] = [Number(a.slice(0, 4)), Number(a.slice(5, 7)), Number(a.slice(8, 10))]
  const [jb, mb, db] = [Number(b.slice(0, 4)), Number(b.slice(5, 7)), Number(b.slice(8, 10))]
  let hele = (jb - ja) * 12 + (mb - ma)
  if (db < da) hele -= 1
  // Het ankerpunt na `hele` maanden (dag geklemd op de maandlengte) en de fractie daarna.
  const ankerMaand = ma - 1 + hele
  const ankerJaar = ja + Math.floor(ankerMaand / 12)
  const ankerM = ((ankerMaand % 12) + 12) % 12
  const dagenInAnker = new Date(Date.UTC(ankerJaar, ankerM + 1, 0)).getUTCDate()
  const anker = Date.UTC(ankerJaar, ankerM, Math.min(da, dagenInAnker))
  const eind = Date.UTC(jb, mb - 1, db)
  const volgende = Date.UTC(ankerJaar, ankerM + 1, Math.min(da, new Date(Date.UTC(ankerJaar, ankerM + 2, 0)).getUTCDate()))
  const fractie = volgende > anker ? (eind - anker) / (volgende - anker) : 0
  return Math.max(0, hele + fractie)
}

export interface ContractTrendInvoer {
  contractStart: Date | null
  contractEind: Date | null
  contractKmPerJaar: number | null
  /** De km-stand bij de start van het contract. */
  contractKmStand: number | null
  /** De huidige (laatste betrouwbare) km-stand. */
  kmStand: number | null
  peil: Date
  meerKmTariefPerKm: number | null
  minderKmTariefPerKm: number | null
  /** Recent weektempo (km per week); zonder: het gemiddelde sinds de contractstart. */
  weekTempo?: number | null
}

export interface ContractTrend {
  cumulatiefKm: number | null
  contractRatoKm: number | null
  afwijkingKm: number | null
  /** Afwijking in procenten van de rato (2 decimalen). */
  afwijkingPct: number | null
  prognoseEindKm: number | null
  contractTotaalKm: number | null
  verwachtMeerKm: number | null
  verwachtMinderKm: number | null
  verwachteMeerKmKosten: number | null
  verwachteMinderKmOpbrengst: number | null
  /** De prognose rekent met het recente weektempo (TREND) of het gemiddelde sinds de start. */
  tempoBron: 'WEEKTEMPO' | 'GEMIDDELDE' | null
}

const LEGE_TREND: ContractTrend = {
  cumulatiefKm: null,
  contractRatoKm: null,
  afwijkingKm: null,
  afwijkingPct: null,
  prognoseEindKm: null,
  contractTotaalKm: null,
  verwachtMeerKm: null,
  verwachtMinderKm: null,
  verwachteMeerKmKosten: null,
  verwachteMinderKmOpbrengst: null,
  tempoBron: null,
}

/**
 * De stand tegen het contract (FO-528 §B6, T4): cumulatief sinds de start, de
 * contract-km naar rato tot vandaag, de afwijking (km en %), de verwachte stand
 * aan het einde van het contract bij het huidige tempo en de meer- of minderkm met
 * het verwachte bedrag (tarief per km). Zonder contract-km of start: geen trend
 * (de lijst "contract-km ontbreekt" toont het). Bedragen worden pas hier, op het
 * laatste moment, afgerond.
 */
export function berekenContractTrend(input: ContractTrendInvoer): ContractTrend {
  if (!input.contractStart || input.contractKmPerJaar == null || input.contractKmPerJaar <= 0) return LEGE_TREND
  const maanden = maandenTussen(input.contractStart, input.peil)
  const ratoKm = Math.round((input.contractKmPerJaar * maanden) / 12)
  const cumulatief = input.kmStand != null && input.contractKmStand != null ? Math.max(0, input.kmStand - input.contractKmStand) : null
  const looptijd = input.contractEind ? maandenTussen(input.contractStart, input.contractEind) : null
  const contractTotaalKm = looptijd != null && looptijd > 0 ? Math.round((input.contractKmPerJaar * looptijd) / 12) : null
  if (cumulatief == null) {
    return { ...LEGE_TREND, contractRatoKm: ratoKm, contractTotaalKm }
  }
  const afwijkingKm = cumulatief - ratoKm
  const afwijkingPct = ratoKm > 0 ? rond2((afwijkingKm / ratoKm) * 100) : null

  let prognoseEindKm: number | null = null
  let tempoBron: ContractTrend['tempoBron'] = null
  if (looptijd != null && looptijd > 0) {
    const resterend = Math.max(0, looptijd - maanden)
    let perMaand: number | null = null
    if (input.weekTempo != null && input.weekTempo >= 0) {
      perMaand = (input.weekTempo * 52) / 12
      tempoBron = 'WEEKTEMPO'
    } else if (maanden > 0) {
      perMaand = cumulatief / maanden
      tempoBron = 'GEMIDDELDE'
    }
    if (perMaand != null) prognoseEindKm = Math.round(cumulatief + perMaand * resterend)
  }

  let verwachtMeerKm: number | null = null
  let verwachtMinderKm: number | null = null
  let kosten: number | null = null
  let opbrengst: number | null = null
  if (prognoseEindKm != null && contractTotaalKm != null) {
    verwachtMeerKm = Math.max(0, prognoseEindKm - contractTotaalKm)
    verwachtMinderKm = Math.max(0, contractTotaalKm - prognoseEindKm)
    kosten = input.meerKmTariefPerKm != null ? rond2(verwachtMeerKm * input.meerKmTariefPerKm) : null
    opbrengst = input.minderKmTariefPerKm != null ? rond2(verwachtMinderKm * input.minderKmTariefPerKm) : null
  }
  return {
    cumulatiefKm: cumulatief,
    contractRatoKm: ratoKm,
    afwijkingKm,
    afwijkingPct,
    prognoseEindKm,
    contractTotaalKm,
    verwachtMeerKm,
    verwachtMinderKm,
    verwachteMeerKmKosten: kosten,
    verwachteMinderKmOpbrengst: opbrengst,
    tempoBron,
  }
}

/** Geeft de trend een signaal "overschrijding verwacht"? (boven het percentage of de kostendrempel) */
export function isContractOverschrijdingVerwacht(
  trend: Pick<ContractTrend, 'afwijkingPct' | 'verwachteMeerKmKosten'>,
  drempels: { contractAfwijkingPct: number; meerKmKostenDrempel: number },
): boolean {
  if (trend.afwijkingPct != null && trend.afwijkingPct > drempels.contractAfwijkingPct) return true
  return trend.verwachteMeerKmKosten != null && trend.verwachteMeerKmKosten > drempels.meerKmKostenDrempel
}

// ─── 5. Verdeling privé/zakelijk ─────────────────────────────────────────────

export interface WeekRit {
  km: number
  classificatie: RitClassificatie
}

export interface WeekVerdeling {
  priveKm: number | null
  zakelijkKm: number | null
  woonWerkKm: number | null
  bron: VerdelingBron
  markeringen: KmTrendMarkering[]
}

/**
 * Verdeel de km van een week over privé en zakelijk (FO-528 §B5, T5/T6).
 *
 *   - TELEMATICA: alle ritten hebben een classificatie (geen ONBEKEND) -> de
 *     verhouding van de ritten, toegepast op de week-km (zo tellen privé en
 *     zakelijk altijd op tot de gemeten week). Woon-werk telt als zakelijk zolang
 *     de instelling dat zegt (A4: zo telt de bijtelling het), anders als privé.
 *   - AFGELEID: zakelijk = de werkkilometers van de week (de ritten van de
 *     werkbonnen) plus woon-werk; privé = de rest. Nooit negatief: zijn er meer
 *     zakelijke km geschreven dan gereden, dan zakelijk = de week, privé 0 en de
 *     markering ZAKELIJK_BOVEN_TOTAAL (een signaal, FO-528 §D).
 *   - GEEN: geen week-km.
 */
export function verdeelWeekKm(input: {
  weekKm: number | null
  ritten: readonly WeekRit[]
  werkKm: number
  woonWerkKm: number
  woonWerkTeltZakelijk: boolean
}): WeekVerdeling {
  if (input.weekKm == null) {
    return { priveKm: null, zakelijkKm: null, woonWerkKm: null, bron: 'GEEN', markeringen: [] }
  }
  const weekKm = Math.max(0, input.weekKm)
  const geclassificeerd = input.ritten.length > 0 && input.ritten.every((r) => r.classificatie !== 'ONBEKEND')
  const ritKm = input.ritten.reduce((s, r) => s + Math.max(0, r.km), 0)
  if (geclassificeerd && ritKm > 0) {
    const som = (c: RitClassificatie) => input.ritten.filter((r) => r.classificatie === c).reduce((s, r) => s + Math.max(0, r.km), 0)
    const woonWerk = som('WOONWERK')
    const zakelijkRuw = som('ZAKELIJK') + (input.woonWerkTeltZakelijk ? woonWerk : 0)
    // Elke rit is privé of zakelijk: de verhouding op de gemeten week-km, privé = de rest.
    const factor = weekKm / ritKm
    const zakelijkKm = rond1(Math.min(weekKm, zakelijkRuw * factor))
    return {
      zakelijkKm,
      priveKm: rond1(Math.max(0, weekKm - zakelijkKm)),
      woonWerkKm: rond1(woonWerk * factor),
      bron: 'TELEMATICA',
      markeringen: [],
    }
  }
  const zakelijk = Math.max(0, input.werkKm) + (input.woonWerkTeltZakelijk ? Math.max(0, input.woonWerkKm) : 0)
  if (zakelijk > weekKm) {
    return { priveKm: 0, zakelijkKm: rond1(weekKm), woonWerkKm: rond1(Math.max(0, input.woonWerkKm)), bron: 'AFGELEID', markeringen: ['ZAKELIJK_BOVEN_TOTAAL'] }
  }
  return {
    priveKm: rond1(weekKm - zakelijk),
    zakelijkKm: rond1(zakelijk),
    woonWerkKm: rond1(Math.max(0, input.woonWerkKm)),
    bron: 'AFGELEID',
    markeringen: [],
  }
}

// ─── 6. Weektempo en onderhoudsdatum ─────────────────────────────────────────

/**
 * Het gemiddelde weektempo van de laatste `max` weken met een km-waarde (gemeten of
 * geïnterpoleerd uit echte standen; GEEN telt niet). Minimaal twee weken, anders null.
 */
export function berekenWeekTempo(
  weken: ReadonlyArray<{ weekSleutel: number; weekKm: number | null; kmBron: KmTrendBron }>,
  max = 8,
): number | null {
  const bruikbaar = [...weken]
    .filter((w) => w.weekKm != null && w.kmBron !== 'GEEN')
    .sort((a, b) => b.weekSleutel - a.weekSleutel)
    .slice(0, max)
  if (bruikbaar.length < 2) return null
  return Math.round(bruikbaar.reduce((s, w) => s + (w.weekKm ?? 0), 0) / bruikbaar.length)
}

/**
 * Wanneer wordt de km-drempel van een onderhoudsbeurt bereikt (FO-528 §C7, T8)?
 * Resterend = vervalKm - huidigeKm; bij een weektempo > 0 is dat over
 * resterend / (tempo / 7) dagen (naar boven afgerond), gerekend vanaf de laatste
 * stand. Al bereikt = de dag van de laatste stand. Geen tempo = onbekend.
 */
export function schatOnderhoudsdatum(input: {
  vervalKm: number
  huidigeKm: number
  weekTempo: number | null
  vanaf: Date
}): { datum: Date | null; weken: number | null } {
  const resterend = input.vervalKm - input.huidigeKm
  if (resterend <= 0) return { datum: new Date(input.vanaf.getTime()), weken: 0 }
  if (input.weekTempo == null || !(input.weekTempo > 0)) return { datum: null, weken: null }
  const dagen = Math.ceil(resterend / (input.weekTempo / 7))
  return { datum: new Date(input.vanaf.getTime() + dagen * DAG_MS), weken: rond1(resterend / input.weekTempo) }
}

// ─── Hulp: postcode-niveau (privacy-arm) ─────────────────────────────────────

/**
 * Het postcode-niveau dat we bewaren en vergelijken: alleen de vier cijfers (PC4, "1234"),
 * nooit de letters. Een volledige postcode (PC6) met een tijdstip wijst ongeveer de straat
 * aan, de eerste rit van de dag dus het woonadres (integrator-review M1). Geen huisnummer,
 * geen GPS; onherkenbaar is null.
 */
export function postcodeNiveau(invoer: string | null | undefined): string | null {
  if (!invoer) return null
  const schoon = invoer.replace(/\s+/g, '').toUpperCase()
  const m = /^(\d{4})(?:[A-Z]{1,2})?$/.exec(schoon)
  return m ? m[1]! : null
}

/**
 * Liggen twee postcodes "op dezelfde plek" (klantlocatie binnen ongeveer 1 km)?
 * Zelfde PC4 = ja (een PC4-gebied is in de stad een paar honderd meter, op het
 * platteland een dorp). Een benadering zonder geocoder.
 */
export function postcodesNabij(a: string | null | undefined, b: string | null | undefined): boolean {
  const pa = postcodeNiveau(a)
  const pb = postcodeNiveau(b)
  if (!pa || !pb) return false
  return pa === pb
}

// ─── 7. Ritten versus uren (FO-528 §D8) ──────────────────────────────────────

export interface DagRit {
  start: Date
  eind: Date
  km: number
  startPostcode: string | null
  eindPostcode: string | null
  classificatie: RitClassificatie
}

export interface DagUren {
  /** HH:mm (NL); bij meerdere dagregels het vroegste begin en het laatste einde. */
  plannedStart: string
  plannedEnd: string
  actualStart: string | null
  actualEnd: string | null
  /** Geschreven reistijd (minuten) en reis-km van de dag (som van de dagregels). */
  reisMinuten: number
  reisKm: number
}

export interface RitUrenDrempels {
  reistijdDrempelMin: number
  reistijdDrempelPct: number
  reisKmDrempel: number
  reisKmDrempelPct: number
  tolerantieMin: number
}

export interface RitUrenBevinding {
  soort:
    | 'REISTIJD_MEER_GESCHREVEN_DAN_GEREDEN'
    | 'REISTIJD_MINDER_GESCHREVEN'
    | 'REISKM_AFWIJKING'
    | 'EERDER_VERTROKKEN'
    | 'TE_LAAT_AANWEZIG'
    | 'AANWEZIGHEID_EERDER_GESCHREVEN'
    | 'REISTIJD_ZONDER_RIT'
  eenheid: 'MIN' | 'KM' | 'TIJD'
  geschreven: number | null
  gemeten: number | null
  verschil: number | null
  geschrevenTekst: string | null
  gemetenTekst: string | null
  hint: 'MOGELIJK_MEEGEREDEN' | null
}

/** Een procent op een paar minuten of kilometers zegt niets: het percentage telt vanaf deze ondergrens. */
const MIN_MINUTEN_VOOR_PCT = 10
const MIN_KM_VOOR_PCT = 5

function hmNaarMinuten(hm: string | null | undefined): number | null {
  if (!hm) return null
  const m = /^(\d{1,2}):(\d{2})/.exec(hm.trim())
  return m ? Number(m[1]) * 60 + Number(m[2]) : null
}

function minutenNaarHm(min: number): string {
  const m = ((Math.round(min) % 1440) + 1440) % 1440
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}

/** Minuten sinds 00:00 NL van `dag` (een rit na middernacht telt door: > 1440). */
function minutenOpDag(moment: Date, dag: string): number {
  return Math.round((moment.getTime() - amsterdamAllDayStartToUtc(dag).getTime()) / 60_000)
}

function boven(verschil: number, basis: number, drempelAbs: number, drempelPct: number, ondergrens: number): boolean {
  if (verschil > drempelAbs) return true
  return basis > 0 && verschil >= ondergrens && (verschil / basis) * 100 > drempelPct
}

/**
 * Vergelijk de ritten van een bestuurder op een dag met zijn geschreven uren en
 * reistijd (FO-528 §D8, T9-T12). Alles is een SIGNAAL, nooit een correctie: de
 * uitkomst gaat naar de bestuurder (toelichten) en de teamleider (beoordelen).
 *
 *   - reistijd: geschreven reisminuten tegen de duur van de niet-privé ritten;
 *   - reis-km: geschreven reis-km tegen de km van de niet-privé ritten;
 *   - eerder vertrokken: de laatste rit vanaf een klantlocatie vertrekt meer dan de
 *     tolerantie vóór het geschreven (of geplande) einde;
 *   - te laat: de eerste aankomst bij een klant ligt na het geplande begin plus de
 *     tolerantie; begin geschreven vóór aankomst: AANWEZIGHEID_EERDER_GESCHREVEN;
 *   - reistijd zonder rit: wel reistijd, geen gereden km (de hint "mogelijk
 *     meegereden" als een collega op dezelfde werkbon wel reed).
 * Klantlocaties en ritten op postcode-niveau (`postcodesNabij`); zonder bekende
 * klantpostcode geen uitspraak over vertrek of aankomst (niet gokken). Ritten over
 * middernacht horen bij hun startdag.
 */
export function vergelijkRitMetUren(input: {
  dag: string
  ritten: readonly DagRit[]
  uren: DagUren
  klantPostcodes: readonly string[]
  collegaReedOpBon: boolean
  drempels: RitUrenDrempels
}): RitUrenBevinding[] {
  const uit: RitUrenBevinding[] = []
  const d = input.drempels
  const zakelijk = [...input.ritten].filter((r) => r.classificatie !== 'PRIVE').sort((a, b) => a.start.getTime() - b.start.getTime())
  const gemetenKm = rond1(zakelijk.reduce((s, r) => s + Math.max(0, r.km), 0))

  // Reistijd zonder rit (T12): wel reistijd geschreven, de auto reed niet.
  if (gemetenKm === 0 && input.uren.reisMinuten > 0) {
    uit.push({
      soort: 'REISTIJD_ZONDER_RIT',
      eenheid: 'MIN',
      geschreven: input.uren.reisMinuten,
      gemeten: 0,
      verschil: input.uren.reisMinuten,
      geschrevenTekst: `${input.uren.reisMinuten} min`,
      gemetenTekst: 'geen rit',
      hint: input.collegaReedOpBon ? 'MOGELIJK_MEEGEREDEN' : null,
    })
    return uit
  }
  if (zakelijk.length === 0) return uit

  // Reistijd (T9).
  const geredenMin = Math.round(zakelijk.reduce((s, r) => s + Math.max(0, r.eind.getTime() - r.start.getTime()), 0) / 60_000)
  const reisVerschil = input.uren.reisMinuten - geredenMin
  if (reisVerschil > 0 && boven(reisVerschil, geredenMin, d.reistijdDrempelMin, d.reistijdDrempelPct, MIN_MINUTEN_VOOR_PCT)) {
    uit.push({
      soort: 'REISTIJD_MEER_GESCHREVEN_DAN_GEREDEN',
      eenheid: 'MIN',
      geschreven: input.uren.reisMinuten,
      gemeten: geredenMin,
      verschil: reisVerschil,
      geschrevenTekst: `${input.uren.reisMinuten} min`,
      gemetenTekst: `${geredenMin} min`,
      hint: null,
    })
  } else if (reisVerschil < 0 && input.uren.reisMinuten > 0 && boven(-reisVerschil, geredenMin, d.reistijdDrempelMin, d.reistijdDrempelPct, MIN_MINUTEN_VOOR_PCT)) {
    uit.push({
      soort: 'REISTIJD_MINDER_GESCHREVEN',
      eenheid: 'MIN',
      geschreven: input.uren.reisMinuten,
      gemeten: geredenMin,
      verschil: reisVerschil,
      geschrevenTekst: `${input.uren.reisMinuten} min`,
      gemetenTekst: `${geredenMin} min`,
      hint: null,
    })
  }

  // Reis-km.
  if (input.uren.reisKm > 0) {
    const kmVerschil = rond1(input.uren.reisKm - gemetenKm)
    if (boven(Math.abs(kmVerschil), gemetenKm, d.reisKmDrempel, d.reisKmDrempelPct, MIN_KM_VOOR_PCT)) {
      uit.push({
        soort: 'REISKM_AFWIJKING',
        eenheid: 'KM',
        geschreven: input.uren.reisKm,
        gemeten: gemetenKm,
        verschil: kmVerschil,
        geschrevenTekst: `${input.uren.reisKm} km`,
        gemetenTekst: `${String(gemetenKm).replace('.', ',')} km`,
        hint: null,
      })
    }
  }

  if (input.klantPostcodes.length === 0) return uit
  const bijKlant = (pc: string | null) => input.klantPostcodes.some((k) => postcodesNabij(pc, k))
  const geplandBegin = hmNaarMinuten(input.uren.plannedStart)
  const geplandEindRuw = hmNaarMinuten(input.uren.plannedEnd)
  const geschrevenBegin = hmNaarMinuten(input.uren.actualStart)
  const geschrevenEindRuw = hmNaarMinuten(input.uren.actualEnd)
  // Een nachtdienst: het einde ligt na middernacht.
  const naMiddernacht = (eind: number | null, begin: number | null) => (eind != null && begin != null && eind < begin ? eind + 1440 : eind)
  const geplandEind = naMiddernacht(geplandEindRuw, geplandBegin)
  const geschrevenEind = naMiddernacht(geschrevenEindRuw, geschrevenBegin ?? geplandBegin)

  // Eerder vertrokken (T10): de laatste rit vanaf een klantlocatie.
  const vertrekken = zakelijk.filter((r) => bijKlant(r.startPostcode))
  const laatsteVertrek = vertrekken[vertrekken.length - 1]
  const eindGeschreven = geschrevenEind ?? geplandEind
  if (laatsteVertrek && eindGeschreven != null) {
    const vertrek = minutenOpDag(laatsteVertrek.start, input.dag)
    if (vertrek < eindGeschreven - d.tolerantieMin) {
      uit.push({
        soort: 'EERDER_VERTROKKEN',
        eenheid: 'TIJD',
        geschreven: eindGeschreven,
        gemeten: vertrek,
        verschil: eindGeschreven - vertrek,
        geschrevenTekst: minutenNaarHm(eindGeschreven),
        gemetenTekst: minutenNaarHm(vertrek),
        hint: null,
      })
    }
  }

  // Te laat aanwezig (T11) en begin geschreven vóór aankomst.
  const aankomst = zakelijk.find((r) => bijKlant(r.eindPostcode))
  if (aankomst) {
    const aan = minutenOpDag(aankomst.eind, input.dag)
    if (geplandBegin != null && aan > geplandBegin + d.tolerantieMin) {
      uit.push({
        soort: 'TE_LAAT_AANWEZIG',
        eenheid: 'TIJD',
        geschreven: geplandBegin,
        gemeten: aan,
        verschil: aan - geplandBegin,
        geschrevenTekst: minutenNaarHm(geplandBegin),
        gemetenTekst: minutenNaarHm(aan),
        hint: null,
      })
    }
    if (geschrevenBegin != null && aan > geschrevenBegin + d.tolerantieMin) {
      uit.push({
        soort: 'AANWEZIGHEID_EERDER_GESCHREVEN',
        eenheid: 'TIJD',
        geschreven: geschrevenBegin,
        gemeten: aan,
        verschil: aan - geschrevenBegin,
        geschrevenTekst: minutenNaarHm(geschrevenBegin),
        gemetenTekst: minutenNaarHm(aan),
        hint: null,
      })
    }
  }
  return uit
}

// ─── Privé-km beoordelen ─────────────────────────────────────────────────────

/**
 * Waarom de privé-km van een week opvallen: boven de weekdrempel (bij elk regime), of
 * privé-km terwijl er geen bijtelling is. Dat laatste geldt ook als de bijtelling niet
 * is vastgelegd: "nee" op het contract is zonder verklaring geen "geen privé", maar de
 * kilometers moeten dan juist wel opvallen. Een indicatie, geen oordeel.
 */
export function beoordeelPriveKm(input: { priveKm: number | null; regime: TankRegime; drempelWeek: number }): PriveKmSignaal[] {
  if (input.priveKm == null || !(input.priveKm > 0)) return []
  const signalen: PriveKmSignaal[] = []
  if (input.priveKm > input.drempelWeek) signalen.push('BOVEN_DREMPEL')
  if (input.regime === 'GEEN_PRIVE') signalen.push('GEEN_BIJTELLING')
  if (input.regime === 'ONBEKEND') signalen.push('BIJTELLING_ONBEKEND')
  return signalen
}

/**
 * De km die volgens werkbonnen en planning voor het werk gereden moesten worden, en de
 * afwijking daarvan. De afwijking bestaat alleen met ritten uit telematica: zonder is
 * "zakelijk" zelf afgeleid uit diezelfde planning, en zou de afwijking altijd 0 lijken.
 */
export function werkKmTegenPlanning(input: {
  werkKm: number | null
  woonWerkKm: number | null
  woonWerkTeltZakelijk: boolean
  zakelijkKm: number | null
  verdelingBron: VerdelingBron
}): { geplandWerkKm: number | null; werkAfwijkingKm: number | null } {
  if (input.werkKm == null) return { geplandWerkKm: null, werkAfwijkingKm: null }
  const gepland = Math.round((input.werkKm + (input.woonWerkTeltZakelijk ? (input.woonWerkKm ?? 0) : 0)) * 10) / 10
  const afwijking = input.verdelingBron === 'TELEMATICA' && input.zakelijkKm != null ? Math.round((input.zakelijkKm - gepland) * 10) / 10 : null
  return { geplandWerkKm: gepland, werkAfwijkingKm: afwijking }
}

// ─── Prognose per contractjaar ───────────────────────────────────────────────

function plusJaren(d: Date, jaren: number): Date {
  const uit = new Date(d)
  uit.setUTCFullYear(uit.getUTCFullYear() + jaren)
  return uit
}

/** Bovengrens: een contract zonder einddatum krijgt alleen jaren tot en met het lopende. */
const MAX_CONTRACT_JAREN = 15

/**
 * De contractjaren: volle jaren vanaf de contractdatum, met per jaar de grens (de
 * contract-km), wat er gereden is en waar het jaar op uitkomt bij het huidige tempo.
 * Gereden komt uit de standen: de contractstand of de stand op de jaargrens tot en met de
 * laatste stand. Ontbreekt een stand op de grens, dan is gereden onbekend (null), nooit 0.
 * Privé en zakelijk tellen alleen de weken met een verdeling; de weken staan erbij.
 */
export function berekenContractJaren(input: {
  contractStart: Date | null
  contractEind: Date | null
  contractKmPerJaar: number | null
  contractKmStand: number | null
  /** Plausibele standen, oplopend in de tijd. */
  standen: readonly KmStandInput[]
  weken: ReadonlyArray<{ weekStart: Date; priveKm: number | null; zakelijkKm: number | null }>
  weekTempo: number | null
  peil: Date
}): ContractJaarDTO[] {
  if (!input.contractStart || input.contractKmPerJaar == null || input.contractKmPerJaar <= 0) return []
  const uit: ContractJaarDTO[] = []
  const totStand = input.standen.filter((s) => s.op.getTime() <= input.peil.getTime())
  for (let i = 0; i < MAX_CONTRACT_JAREN; i++) {
    const start = plusJaren(input.contractStart, i)
    if (input.contractEind && start.getTime() >= input.contractEind.getTime()) break
    if (!input.contractEind && start.getTime() > input.peil.getTime()) break
    const volEind = plusJaren(input.contractStart, i + 1)
    const eind = input.contractEind && input.contractEind.getTime() < volEind.getTime() ? input.contractEind : volEind
    const lengte = eind.getTime() - start.getTime()
    const volLengte = volEind.getTime() - start.getTime()
    const grensKm = Math.round((input.contractKmPerJaar * lengte) / volLengte)
    const status: ContractJaarStatus =
      start.getTime() > input.peil.getTime() ? 'TOEKOMST' : eind.getTime() <= input.peil.getTime() ? 'AFGEROND' : 'LOPEND'

    const beginStand = i === 0 ? input.contractKmStand : (kmStandOpMoment(input.standen, start)?.km ?? null)
    let eindStand: number | null = null
    if (status === 'AFGEROND') eindStand = kmStandOpMoment(input.standen, eind)?.km ?? null
    if (status === 'LOPEND') {
      const laatste = totStand[totStand.length - 1]
      eindStand = laatste && laatste.op.getTime() >= start.getTime() ? laatste.kmStand : null
    }
    const geredenKm = status !== 'TOEKOMST' && beginStand != null && eindStand != null ? Math.max(0, Math.round(eindStand - beginStand)) : null

    let prognoseKm: number | null = status === 'AFGEROND' ? geredenKm : null
    let tempoBron: ContractJaarDTO['tempoBron'] = null
    if (status === 'LOPEND' && geredenKm != null) {
      const verstrekenWeken = (input.peil.getTime() - start.getTime()) / (7 * DAG_MS)
      const restWeken = (eind.getTime() - input.peil.getTime()) / (7 * DAG_MS)
      if (input.weekTempo != null && input.weekTempo >= 0) {
        prognoseKm = Math.round(geredenKm + input.weekTempo * restWeken)
        tempoBron = 'WEEKTEMPO'
      } else if (verstrekenWeken >= 1) {
        prognoseKm = Math.round(geredenKm + (geredenKm / verstrekenWeken) * restWeken)
        tempoBron = 'GEMIDDELDE'
      }
    }

    let priveKm = 0
    let zakelijkKm = 0
    let wekenMetVerdeling = 0
    for (const w of input.weken) {
      const t = w.weekStart.getTime()
      if (t < start.getTime() || t >= eind.getTime()) continue
      if (w.priveKm == null && w.zakelijkKm == null) continue
      priveKm += w.priveKm ?? 0
      zakelijkKm += w.zakelijkKm ?? 0
      wekenMetVerdeling++
    }
    uit.push({
      jaar: i + 1,
      start: start.toISOString(),
      eind: eind.toISOString(),
      status,
      grensKm,
      geredenKm,
      prognoseKm,
      verschilKm: prognoseKm == null ? null : prognoseKm - grensKm,
      tempoBron,
      priveKm: Math.round(priveKm),
      zakelijkKm: Math.round(zakelijkKm),
      wekenMetVerdeling,
    })
  }
  return uit
}

// ─── Advies bij een structurele afwijking in de werk-km ──────────────────────

/**
 * Wijken de werk-km de laatste `minWeken` weken op rij af? Met ritten: gemeten tegen
 * gepland. Zonder ritten is alleen meetbaar dat er meer werk gepland staat dan de auto
 * reed. Een week zonder gegevens of een gat in de reeks breekt de reeks. Het advies is een
 * voorstel: een mens beslist of en wat er gecorrigeerd wordt.
 */
export function bepaalWerkAdvies(
  weken: ReadonlyArray<{ weekSleutel: number; weekKm: number | null; geplandWerkKm: number | null; werkAfwijkingKm: number | null }>,
  opties: { minWeken: number; minKm: number },
): { soort: WerkAdviesSoort; weken: number; gemiddeldKm: number } | null {
  if (opties.minWeken < 1 || weken.length < opties.minWeken) return null
  const laatste = [...weken].sort((a, b) => a.weekSleutel - b.weekSleutel).slice(-opties.minWeken)
  for (let i = 1; i < laatste.length; i++) {
    if (volgendeWeekSleutel(laatste[i - 1]!.weekSleutel) !== laatste[i]!.weekSleutel) return null
  }
  const gemiddelde = (w: number[]) => Math.round((w.reduce((s, x) => s + x, 0) / w.length) * 10) / 10
  const afwijkingen = laatste.map((w) => w.werkAfwijkingKm)
  if (afwijkingen.every((a): a is number => a != null)) {
    if (afwijkingen.every((a) => a >= opties.minKm)) return { soort: 'MEER_DAN_GEPLAND', weken: laatste.length, gemiddeldKm: gemiddelde(afwijkingen) }
    if (afwijkingen.every((a) => a <= -opties.minKm)) return { soort: 'MINDER_DAN_GEPLAND', weken: laatste.length, gemiddeldKm: gemiddelde(afwijkingen.map(Math.abs)) }
    return null
  }
  const boven: number[] = []
  for (const w of laatste) {
    if (w.weekKm == null || w.geplandWerkKm == null || w.geplandWerkKm - w.weekKm < opties.minKm) return null
    boven.push(w.geplandWerkKm - w.weekKm)
  }
  return { soort: 'GEPLAND_BOVEN_GEREDEN', weken: laatste.length, gemiddeldKm: gemiddelde(boven) }
}
